import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { has } from 'ramda'

import PlainCheckbox, { LevelShift } from 'app/common/ui-next/plain-checkbox'
import { PlainInputNumber } from 'app/common/ui-next/plain-input'
import PlainLabel from 'app/common/ui-next/plain-label'
import PlainFieldset from 'app/common/ui-next/plain-fieldset'
import { useGeneralSettings } from 'app/dataProviders/generalSettings/hooks'

import styles from './AdditionalSettingsForm.scss'
import { Loading } from 'app/common/templates-next/page-template'

type CurrentValues = {
  buffetZone: boolean
  hotMealsZone: boolean
  denyParentMoneySales: boolean
  denyMoreThanOneSales: boolean
  disableForTerminalGuestSales: boolean
  useAsAdditional: boolean
}

const defaultCurrent: CurrentValues = {
  buffetZone: true,
  hotMealsZone: true,
  denyParentMoneySales: false,
  denyMoreThanOneSales: false,
  disableForTerminalGuestSales: false,
  useAsAdditional: false,
}

type Props = {
  buffetZone: boolean
  hotMealsZone: boolean
  denyParentMoneySales: boolean
  denyMoreThanOneSales: boolean
  disableForTerminalGuestSales: boolean
  useAsAdditional: boolean
  fundMovementTypeCode?: number | null
  disabled?: boolean
}

const AdditionalSettingsForm = ({
  buffetZone,
  hotMealsZone,
  denyParentMoneySales,
  denyMoreThanOneSales,
  disableForTerminalGuestSales,
  useAsAdditional,
  fundMovementTypeCode,
  disabled,
}: Props) => {
  const [fundMovementTypeCodeValue, setFundMovementTypeCodeValue] = useState(
    String(fundMovementTypeCode ?? ''),
  )

  useEffect(() => {
    setFundMovementTypeCodeValue(String(fundMovementTypeCode ?? ''))
  }, [fundMovementTypeCode])

  const handleFundMovementTypeCodeChange = useCallback((value: string) => {
    setFundMovementTypeCodeValue(value)
  }, [])

  const [currentValues, setCurrentValues] = useState({
    buffetZone,
    hotMealsZone,
    denyParentMoneySales,
    denyMoreThanOneSales,
    disableForTerminalGuestSales,
    useAsAdditional,
  })

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.currentTarget
    if (has(name, defaultCurrent)) {
      setCurrentValues((prevState) => ({
        ...prevState,
        [name]: checked,
      }))
    }
  }, [])

  const {
    generalSettings: { useCommonOrderComplexChoose },
    loading: loadingGeneralSettings,
  } = useGeneralSettings()

  useEffect(() => {
    setCurrentValues({
      buffetZone,
      hotMealsZone,
      denyParentMoneySales,
      denyMoreThanOneSales,
      disableForTerminalGuestSales,
      useAsAdditional,
    })
  }, [
    buffetZone,
    hotMealsZone,
    denyParentMoneySales,
    denyMoreThanOneSales,
    disableForTerminalGuestSales,
    useAsAdditional,
  ])

  return !loadingGeneralSettings ? (
    <div className={styles.root}>
      <PlainFieldset className={styles.sellPlaceGroup} legend="Место продажи">
        <LevelShift>
          <PlainCheckbox
            label="зона буфета"
            name="buffetZone"
            value="active"
            checked={currentValues.buffetZone}
            onChange={handleChange}
            disabled={disabled}
          />
          <PlainCheckbox
            label="зона горячего питания"
            name="hotMealsZone"
            value="active"
            checked={currentValues.hotMealsZone}
            onChange={handleChange}
            disabled={disabled}
          />
        </LevelShift>
      </PlainFieldset>

      <PlainCheckbox
        label="Не продавать за родительские средства"
        name="denyParentMoneySales"
        value="active"
        checked={currentValues.denyParentMoneySales}
        onChange={handleChange}
        disabled={disabled}
      />
      <PlainCheckbox
        label="Не продавать более 1 шт."
        name="denyMoreThanOneSales"
        value="active"
        checked={currentValues.denyMoreThanOneSales}
        onChange={handleChange}
        disabled={disabled}
      />
      <PlainCheckbox
        label="Не продавать гостям в терминале самообслуживания"
        name="disableForTerminalGuestSales"
        value="active"
        checked={currentValues.disableForTerminalGuestSales}
        onChange={handleChange}
        disabled={disabled}
      />

      {useCommonOrderComplexChoose && (
        <PlainCheckbox
          label="Использовать как дополнительный комплект при создании справок с выбором «один из множества»"
          name="useAsAdditional"
          value="active"
          checked={currentValues.useAsAdditional}
          onChange={handleChange}
          disabled={disabled}
        />
      )}
      <PlainLabel htmlFor="fundMovementTypeCode" displayAsBlock skipUpperSpace>
        {'Код вида движения средств'}
      </PlainLabel>
      <LevelShift>
        <PlainInputNumber
          className={styles.codeInput}
          id="fundMovementTypeCode"
          name="fundMovementTypeCode"
          value={fundMovementTypeCodeValue}
          min="1"
          max="99"
          step="1"
          placeholder="От 1 до 99"
          onValue={handleFundMovementTypeCodeChange}
          disabled={disabled}
        />
      </LevelShift>
    </div>
  ) : (
    <div className={styles.root}>
      <Loading className={styles.loading} />
    </div>
  )
}

export default AdditionalSettingsForm
